import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LeadTableRow = ({ lead, onViewDetails, onUpdateStatus, onAddNote }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStageColor = (stage) => {
    const colors = {
      new: 'var(--color-accent)',
      contacted: '#3B82F6',
      qualified: '#8B5CF6',
      proposal: '#F59E0B',
      negotiation: '#10B981',
      closed_won: 'var(--color-success)',
      closed_lost: 'var(--color-error)'
    };
    return colors?.[stage] || 'var(--color-muted-foreground)';
  };

  const getInterestIcon = (level) => {
    const icons = {
      hot: 'Flame',
      warm: 'Sun',
      cold: 'Snowflake'
    };
    return icons?.[level] || 'Circle';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  return (
    <>
      {/* Desktop View */}
      <tr className="hidden lg:table-row border-b border-border hover:bg-muted/50 transition-smooth">
        <td className="px-4 py-4">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
              <span className="text-primary font-medium text-sm">
                {lead?.nombre?.split(' ')?.map(n => n?.[0])?.join('')?.substring(0, 2)}
              </span>
            </div>
            <div>
              <div className="font-medium text-foreground">{lead?.nombre}</div>
              <div className="text-xs text-muted-foreground">{lead?.email}</div>
            </div>
          </div>
        </td>
        <td className="px-4 py-4">
          <div className="text-sm text-foreground">{lead?.telefono}</div>
          <div className="text-xs text-muted-foreground">{lead?.fuente}</div>
        </td>
        <td className="px-4 py-4">
          <div className="text-sm text-foreground">{lead?.proyectoInteres}</div>
          <div className="text-xs text-muted-foreground">
            {formatCurrency(lead?.presupuesto)}
          </div>
        </td>
        <td className="px-4 py-4">
          <div className="flex items-center">
            <div 
              className="w-3 h-3 rounded-full mr-2"
              style={{ backgroundColor: getStageColor(lead?.stage) }}
            />
            <span className="text-sm text-foreground capitalize">
              {lead?.stageLabel}
            </span>
          </div>
        </td>
        <td className="px-4 py-4">
          <div className="flex items-center">
            <Icon 
              name={getInterestIcon(lead?.nivelInteres)} 
              size={16} 
              className="mr-2"
              color={
                lead?.nivelInteres === 'hot' ? '#EF4444' :
                lead?.nivelInteres === 'warm'? '#F59E0B' : '#6B7280'
              }
            />
            <span className="text-sm text-foreground capitalize">
              {lead?.nivelInteresLabel}
            </span>
          </div>
        </td>
        <td className="px-4 py-4">
          <div className="text-sm text-foreground">{formatDate(lead?.ultimaActividad)}</div>
          <div className="text-xs text-muted-foreground">{lead?.proximoSeguimiento}</div>
        </td>
        <td className="px-4 py-4">
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              iconName="Eye"
              onClick={() => onViewDetails(lead)}
            />
            <Button
              variant="ghost"
              size="icon"
              iconName="MessageSquare"
              onClick={() => onAddNote(lead)}
            />
            <Button
              variant="ghost"
              size="icon"
              iconName="RefreshCw"
              onClick={() => onUpdateStatus(lead)}
            />
          </div>
        </td>
      </tr>
      {/* Mobile View */}
      <div className="lg:hidden bg-card rounded-xl p-4 mb-3 border border-border">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center flex-1">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mr-3">
              <span className="text-primary font-medium">
                {lead?.nombre?.split(' ')?.map(n => n?.[0])?.join('')?.substring(0, 2)}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-foreground truncate">{lead?.nombre}</div>
              <div className="text-xs text-muted-foreground truncate">{lead?.email}</div>
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon 
              name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
              size={20} 
              color="var(--color-foreground)"
            />
          </button>
        </div>

        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <div 
              className="w-3 h-3 rounded-full mr-2"
              style={{ backgroundColor: getStageColor(lead?.stage) }}
            />
            <span className="text-sm text-foreground capitalize">
              {lead?.stageLabel}
            </span>
          </div>
          <div className="flex items-center">
            <Icon 
              name={getInterestIcon(lead?.nivelInteres)} 
              size={16} 
              className="mr-2"
              color={
                lead?.nivelInteres === 'hot' ? '#EF4444' :
                lead?.nivelInteres === 'warm'? '#F59E0B' : '#6B7280'
              }
            />
            <span className="text-sm text-foreground capitalize">
              {lead?.nivelInteresLabel}
            </span>
          </div>
        </div>

        {isExpanded && (
          <div className="space-y-3 pt-3 border-t border-border">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Contacto</div>
              <div className="text-sm text-foreground">{lead?.telefono}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Proyecto de Interés</div>
              <div className="text-sm text-foreground">{lead?.proyectoInteres}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Presupuesto</div>
              <div className="text-sm text-foreground">{formatCurrency(lead?.presupuesto)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Última Actividad</div>
              <div className="text-sm text-foreground">{formatDate(lead?.ultimaActividad)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Próximo Seguimiento</div>
              <div className="text-sm text-foreground">{lead?.proximoSeguimiento}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Fuente</div>
              <div className="text-sm text-foreground capitalize">{lead?.fuente}</div>
            </div>
          </div>
        )}

        <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            iconName="Eye"
            iconPosition="left"
            onClick={() => onViewDetails(lead)}
            fullWidth
          >
            Ver
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="MessageSquare"
            iconPosition="left"
            onClick={() => onAddNote(lead)}
            fullWidth
          >
            Nota
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={() => onUpdateStatus(lead)}
            fullWidth
          >
            Estado
          </Button>
        </div>
      </div>
    </>
  );
};

export default LeadTableRow;